/**
 * LiveSessionSchedulerPage.jsx — Trainer / Admin scheduler.
 * Lets schedulers create + manage live classroom sessions.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import {
  createLiveSession, listLiveSessions, trainerConsole, formatApiError,
} from "../../lib/liveClassApi";
import { toast } from "sonner";
import {
  CalendarRange, Save, Plus, Radio, ArrowLeft, Users, Sparkles,
  ListChecks, Crown, Loader2,
} from "lucide-react";

const FIELD = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81]/20";
const LABEL = "text-[11px] uppercase tracking-[0.22em] font-bold text-slate-600";

const defaultStart = () => {
  const d = new Date(Date.now() + 30 * 60 * 1000);
  d.setSeconds(0, 0);
  return d.toISOString().slice(0, 16);
};
const defaultEnd = () => {
  const d = new Date(Date.now() + 90 * 60 * 1000);
  d.setSeconds(0, 0);
  return d.toISOString().slice(0, 16);
};

export default function LiveSessionSchedulerPage() {
  const [form, setForm] = useState({
    title: "", description: "",
    start_time: defaultStart(), end_time: defaultEnd(),
    timezone: "UTC", co_host_ids: "", enrolled_user_ids: "",
    visibility: "enrolled", recording_enabled: true, auto_record: true,
  });
  const [creating, setCreating] = useState(false);
  const [console_, setConsole] = useState(null);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  const reload = async () => {
    try {
      const [c, all] = await Promise.all([
        trainerConsole(),
        listLiveSessions({ upcoming_only: false, limit: 50 }),
      ]);
      setConsole(c);
      setRecent(all);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      toast.error("Session title required");
      return;
    }
    setCreating(true);
    try {
      const ids = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
      const payload = {
        title: form.title, description: form.description,
        start_time: new Date(form.start_time).toISOString(),
        end_time:   new Date(form.end_time).toISOString(),
        timezone: form.timezone,
        co_host_ids: ids(form.co_host_ids),
        enrolled_user_ids: ids(form.enrolled_user_ids),
        visibility: form.visibility,
        recording_enabled: form.recording_enabled,
        auto_record: form.auto_record,
      };
      const s = await createLiveSession(payload);
      toast.success("Live classroom scheduled");
      navigate(`/dashboard/lms/live-sessions/${s.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  return (
    <DashboardLayout title="Schedule Live Classroom">
      <div data-testid="lmslive-scheduler" className="space-y-6 pb-12">
        <section
          data-testid="lmslive-scheduler-hero"
          className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white"
        >
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative">
            <Link to="/dashboard/lms/live-sessions" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]/90 hover:text-white">
              <ArrowLeft size={12} /> Live Classroom
            </Link>
            <p className="mt-3 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Crown size={12} /> Trainer · Diplomexa Academy
            </p>
            <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">Schedule a Live Classroom</h1>
            <p className="mt-2 max-w-2xl text-sm text-blue-100">
              Create a Diplomexa Live Classroom session. Recording is on by default —
              learners can re-watch missed classes in the Academy.
            </p>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <main className="lg:col-span-8">
            <form
              onSubmit={submit}
              data-testid="lmslive-scheduler-form"
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4"
            >
              <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F]">Session details</h2>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className={LABEL}>Title</label>
                  <input
                    value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="lmslive-scheduler-title" className={`mt-1 ${FIELD}`}
                    placeholder="e.g. UNGA Crisis Drill"
                    required minLength={2} maxLength={200}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL}>Description (optional)</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="lmslive-scheduler-description" className={`mt-1 ${FIELD} min-h-[80px]`}
                  />
                </div>
                <div>
                  <label className={LABEL}>Starts at</label>
                  <input type="datetime-local" value={form.start_time}
                          onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                          data-testid="lmslive-scheduler-start" className={`mt-1 ${FIELD}`} required />
                </div>
                <div>
                  <label className={LABEL}>Ends at</label>
                  <input type="datetime-local" value={form.end_time}
                          onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                          data-testid="lmslive-scheduler-end" className={`mt-1 ${FIELD}`} required />
                </div>
                <div>
                  <label className={LABEL}>Visibility</label>
                  <select value={form.visibility}
                            onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                            data-testid="lmslive-scheduler-visibility" className={`mt-1 ${FIELD}`}>
                    <option value="enrolled">Enrolled learners only</option>
                    <option value="open">Open to all org delegates</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Timezone</label>
                  <input value={form.timezone}
                          onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                          data-testid="lmslive-scheduler-timezone" className={`mt-1 ${FIELD}`} />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL}>Co-host user IDs (comma-separated, optional)</label>
                  <input value={form.co_host_ids}
                          onChange={(e) => setForm({ ...form, co_host_ids: e.target.value })}
                          data-testid="lmslive-scheduler-cohosts" className={`mt-1 ${FIELD}`}
                          placeholder="user-id-1, user-id-2" />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL}>Enrolled learner IDs (comma-separated)</label>
                  <input value={form.enrolled_user_ids}
                          onChange={(e) => setForm({ ...form, enrolled_user_ids: e.target.value })}
                          data-testid="lmslive-scheduler-enrolled" className={`mt-1 ${FIELD}`}
                          placeholder="delegate-1, delegate-2…" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                  <input type="checkbox" checked={form.recording_enabled}
                          onChange={(e) => setForm({ ...form, recording_enabled: e.target.checked })}
                          data-testid="lmslive-scheduler-recording-toggle" />
                  <span className="text-xs font-semibold text-[#0A192F]">
                    Enable recording
                  </span>
                </label>
                <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                  <input type="checkbox" checked={form.auto_record}
                          onChange={(e) => setForm({ ...form, auto_record: e.target.checked })}
                          data-testid="lmslive-scheduler-autorec-toggle"
                          disabled={!form.recording_enabled} />
                  <span className="text-xs font-semibold text-[#0A192F]">
                    Auto-record when host joins
                  </span>
                </label>
              </div>
              <button
                type="submit"
                disabled={creating}
                data-testid="lmslive-scheduler-submit"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A192F] px-4 py-2 text-[12px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50] disabled:opacity-50"
              >
                {creating ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Schedule classroom
              </button>
            </form>
          </main>

          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                <CalendarRange size={14} className="text-[#D4AF37]" /> Upcoming
              </h3>
              <div className="mt-3 space-y-2">
                {(console_?.upcoming_sessions || []).slice(0, 5).map(s => (
                  <Link key={s.id} to={`/dashboard/lms/live-sessions/${s.id}`}
                          data-testid={`lmslive-scheduler-upcoming-${s.id}`}
                          className="block rounded-lg border border-slate-200 px-3 py-2 hover:border-[#D4AF37]/60">
                    <p className="text-xs font-semibold text-[#0A192F] truncate">{s.title}</p>
                    <p className="text-[10px] text-slate-500">{new Date(s.start_time).toLocaleString()}</p>
                  </Link>
                ))}
                {!console_?.upcoming_sessions?.length ? (
                  <p className="text-[11px] text-slate-500">No upcoming sessions.</p>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                <ListChecks size={14} className="text-[#D4AF37]" /> Recent in your org
              </h3>
              <div className="mt-3 space-y-2 max-h-72 overflow-auto">
                {recent.slice(0, 10).map(s => (
                  <Link key={s.id} to={`/dashboard/lms/live-sessions/${s.id}`}
                          data-testid={`lmslive-scheduler-recent-${s.id}`}
                          className="block rounded-lg border border-slate-200 px-3 py-2 hover:border-[#D4AF37]/60">
                    <p className="text-xs font-semibold text-[#0A192F] truncate">{s.title}</p>
                    <p className="text-[10px] text-slate-500 uppercase">{s.status}</p>
                  </Link>
                ))}
                {!recent.length ? (
                  <p className="text-[11px] text-slate-500">No sessions yet.</p>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}
